import { call, put, select } from 'redux-saga/effects';
import { saveTaxRatesSuccess } from '../actions';
import { updateOrganisationTaxRates } from 'store/organisation/actions';
import { getOrgOrganisations } from 'store/organisation/selectors';
import { Organisation } from 'store/organisation/models';
import { Provider } from 'store/api/enums';
import { getTaxRates, GetTaxRatesResponse } from 'store/returns/api';
import { RefreshConnectionTaxRatesRequestAction } from '../types';
import { ApiError } from 'store/api/types';
import { checkResponse } from 'store/utils';
import { logoutRequest } from 'store/auth/actions';
import { renderErrorMessage } from 'utils/render-error-message';
import { MessageData } from 'store/app/types';
import { turnMessageOn } from 'store/app/actions';
import { TaxRate } from 'store/returns/models/returnData';

const parseTaxRatesPayload = (
  response: GetTaxRatesResponse
): { taxRates: TaxRate[]; taxEnabled: boolean } => {
  const inner: any = (response as any)?.data ?? response;
  return {
    taxRates: inner?.taxRates ?? [],
    taxEnabled: inner?.taxEnabled ?? false,
  };
};

/* eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types */
export function* refreshConnectionTaxRatesSaga(
  action: RefreshConnectionTaxRatesRequestAction
) {
  try {
    const { connectionId, suppressMessages } = action.payload;

    const response: GetTaxRatesResponse = yield call(getTaxRates, connectionId);
    const err: ApiError | null = checkResponse(response?.responseMessage);
    if (err) throw err;

    const { taxRates: freshTaxRates, taxEnabled: freshTaxEnabled } =
      parseTaxRatesPayload(response);

    const organisations: Organisation[] | undefined = yield select(
      getOrgOrganisations
    );
    const org = organisations?.find((o) => o.connectionId === connectionId);
    const isQuickBooks =
      org?.providerName?.toLowerCase() === Provider.QUICKBOOKS.toLowerCase();

    let taxRatesUnique: TaxRate[] = [...freshTaxRates];
    if (isQuickBooks) {
      const uniqueResponse: GetTaxRatesResponse = yield call(getTaxRates, connectionId, {
        separatePurchasesAndSales: false,
      });
      const uniqueErr: ApiError | null = checkResponse(
        uniqueResponse?.responseMessage
      );
      if (uniqueErr) throw uniqueErr;
      taxRatesUnique = parseTaxRatesPayload(uniqueResponse).taxRates;
    }

    yield put(saveTaxRatesSuccess(freshTaxRates, connectionId));
    yield put(
      updateOrganisationTaxRates(
        connectionId,
        freshTaxRates,
        freshTaxEnabled,
        taxRatesUnique
      )
    );
  } catch (error: any) {
    const message: MessageData | null = renderErrorMessage(error);
    if (message && !action.payload?.suppressMessages) {
      yield put(turnMessageOn(message));
    } else if (!message) {
      yield put(logoutRequest());
    }
  }
}
